import { CalendarIcon } from 'lucide-react';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Calendar, CalendarProps } from '@/components/ui/calendar';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { computeEndMonth } from './compute-end-month';

type MultipleDatePickerFieldProps = {
  form: any;
  fieldName: string;
  label?: string;
  placeholder: string;
  inline: boolean;
  description?: string;
  align: React.ComponentProps<typeof PopoverContent>['align'];
  calendarProps: Omit<
    Extract<CalendarProps, { mode: 'multiple' }>,
    'mode' | 'selected' | 'onSelect'
  >;
};

/**
 * Coerces a react-hook-form value into the `Date[]` react-day-picker expects in
 * multiple mode. Guards against legacy form state holding a single `Date`.
 *
 * @param value - the raw field value
 * @returns the picked dates, always an array
 */
const toDateList = (value: unknown): Date[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return value instanceof Date ? [value] : [];
};

/**
 * React-hook-form field picking an arbitrary number of dates. The trigger shows
 * how many dates are picked; the list itself is rendered by the caller (see
 * `SelectedDatesCard`).
 *
 * @param props.form - the react-hook-form instance
 * @param props.fieldName - name of the bound form field, holding a `Date[]`
 * @param props.label - optional field label
 * @param props.placeholder - i18n key shown while no date is picked
 * @param props.inline - lay the label and trigger on one row
 * @param props.description - optional field description
 * @param props.align - popover alignment
 * @param props.calendarProps - forwarded to the underlying calendar
 * @returns the form-bound multiple date picker
 */
export function MultipleDatePickerField({
  form,
  fieldName,
  label,
  placeholder,
  inline,
  description,
  align,
  calendarProps,
}: MultipleDatePickerFieldProps) {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        const selected = toDateList(field.value);

        return (
          <FormItem>
            <div
              className={
                inline
                  ? 'flex flex-row items-center justify-between gap-2 space-y-0'
                  : 'flex flex-col gap-2'
              }
            >
              {label && <FormLabel>{label}</FormLabel>}
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        inline ? 'w-full md:w-[240px]' : 'w-full',
                        'gap-2 pl-3 text-left font-normal',
                        selected.length === 0 && 'text-muted-foreground',
                      )}
                    >
                      <span>
                        {selected.length === 0
                          ? t(placeholder)
                          : t('datepicker-form.selected-count', {
                              count: selected.length,
                            })}
                      </span>
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>

                <PopoverContent
                  className="w-auto p-0"
                  align={align}
                  alignOffset={20}
                >
                  <Calendar
                    mode="multiple"
                    selected={selected}
                    onSelect={(dates) => field.onChange(dates ?? [])}
                    autoFocus={true}
                    defaultMonth={selected[0] ?? calendarProps.defaultMonth}
                    {...calendarProps}
                    endMonth={computeEndMonth(selected[0] ?? null)}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
